import {
  HttpClient,
  HttpContext,
  HttpErrorResponse,
  HttpHeaders,
  HttpParams,
} from '@angular/common/http';
import { map, Observable, throwError } from 'rxjs';
import { catchError, timeout } from 'rxjs/operators';
import { inject, InjectionToken } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

import {
  I18nHelperInterface,
  RestData,
  SingleRestData,
} from './rest-client-base.models';

export const API_URL_TOKEN = new InjectionToken<string>('API_URL_TOKEN');
export const SESSION_TOKEN_NAME = new InjectionToken<string>(
  'SESSION_TOKEN_NAME'
);
export const I18N_HELPER_BASE = new InjectionToken<I18nHelperInterface>(
  'I18N_HELPER_BASE'
);

// Type helper to unwrap REST response wrappers
type UnwrapRestData<T> = T extends SingleRestData<infer U>
  ? U
  : T extends RestData<infer U>
  ? U[]
  : T;

export class RestClientBase {
  private sanitizer = inject(DomSanitizer);

  // Configuration constants
  private readonly DEFAULT_TIMEOUT = 30000; // 30 seconds

  static ERROR_API_NOT_AVAILABLE =
    'The application server could not be accessed. This is a networking error.';

  static ERROR_CODE_START = 399;
  static HTTP_UNAUTHORIZED = 401;
  static HTTP_FORBIDDEN = 403;
  static HTTP_NOT_FOUND = 404;
  static HTTP_BAD_GATEWAY = 502;
  static HTTP_BAD_REQUEST = 400;
  static HTTP_CONFLICT = 409;
  static HTTP_PRECONDITION_REQUIRED = 428;
  static HTTP_GETAWAY_TIMEOUT = 504;

  protected readonly http = inject(HttpClient);
  protected readonly apiUrl = inject(API_URL_TOKEN);

  get<T extends SingleRestData<unknown> | RestData<unknown>>(
    url: string,
    options?: {
      headers?:
        | HttpHeaders
        | {
            [header: string]: string | string[];
          };
      context?: HttpContext;
      observe?: 'body';
      params?:
        | HttpParams
        | {
            [param: string]:
              | string
              | number
              | boolean
              | ReadonlyArray<string | number | boolean>;
          };
      reportProgress?: boolean;
      responseType?: 'json';
      withCredentials?: boolean;
    }
  ): Observable<UnwrapRestData<T>> {
    const requestOptions = this.addSSRHeader({
      ...options,
      observe: 'body' as const,
      responseType: 'json' as const,
    });

    return this.http
      .get<T>(this.apiURL(url), { ...requestOptions, observe: 'body' as const })
      .pipe(
        timeout(this.DEFAULT_TIMEOUT),
        catchError(this.handleError),
        map((response: T): UnwrapRestData<T> => {
          // Auto-unwrap SingleRestData<T> and RestData<T>
          if (response && typeof response === 'object' && 'data' in response) {
            return response.data as UnwrapRestData<T>;
          }
          return response as UnwrapRestData<T>;
        })
      );
  }

  getBlob(
    url: string,
    options?: {
      headers?:
        | HttpHeaders
        | {
            [header: string]: string | string[];
          };
      context?: HttpContext;
      observe?: 'body';
      params?:
        | HttpParams
        | {
            [param: string]:
              | string
              | number
              | boolean
              | ReadonlyArray<string | number | boolean>;
          };
      reportProgress?: boolean;
      withCredentials?: boolean;
      transferCache?: boolean;
      timeout?: number;
    }
  ): Observable<Blob> {
    const requestOptions = {
      ...options,
      responseType: 'blob' as const,
    };

    return this.http
      .get(this.apiURL(url), requestOptions)
      .pipe(timeout(this.DEFAULT_TIMEOUT), catchError(this.handleError));
  }

  getHtml(
    url: string,
    options?: {
      headers?:
        | HttpHeaders
        | {
            [header: string]: string | string[];
          };
      context?: HttpContext;
      observe?: 'body';
      params?:
        | HttpParams
        | {
            [param: string]:
              | string
              | number
              | boolean
              | ReadonlyArray<string | number | boolean>;
          };
      reportProgress?: boolean;
      withCredentials?: boolean;
      transferCache?: boolean;
      timeout?: number;
    }
  ): Observable<SafeHtml> {
    const requestOptions = {
      ...options,
      responseType: 'text' as const,
    };

    return this.http.get(url, requestOptions).pipe(
      timeout(this.DEFAULT_TIMEOUT),
      catchError(this.handleError),
      map(
        (result: string) =>
          this.sanitizer.bypassSecurityTrustHtml(result) as SafeHtml
      ),
      catchError(this.handleError)
    );
  }

  post<T extends SingleRestData<unknown> | RestData<unknown>>(
    url: string,
    body: unknown | null,
    options?: {
      headers?:
        | HttpHeaders
        | {
            [header: string]: string | string[];
          };
      context?: HttpContext;
      observe?: 'body';
      params?:
        | HttpParams
        | {
            [param: string]:
              | string
              | number
              | boolean
              | ReadonlyArray<string | number | boolean>;
          };
      reportProgress?: boolean;
      responseType?: 'json';
      withCredentials?: boolean;
      timeout?: number;
    }
  ): Observable<UnwrapRestData<T>> {
    const requestOptions = this.addSSRHeader({
      ...options,
      observe: 'body' as const,
      responseType: 'json' as const,
      timeout: options?.timeout || this.DEFAULT_TIMEOUT,
    });

    return this.http
      .post<T>(this.apiURL(url), body, {
        ...requestOptions,
        observe: 'body' as const,
      })
      .pipe(
        timeout(this.DEFAULT_TIMEOUT),
        catchError(this.handleError),
        map((response: T): UnwrapRestData<T> => {
          // Auto-unwrap SingleRestData<T> and RestData<T>
          if (response && typeof response === 'object' && 'data' in response) {
            return response.data as UnwrapRestData<T>;
          }
          return response as UnwrapRestData<T>;
        })
      );
  }

  put<T extends SingleRestData<unknown> | RestData<unknown>>(
    url: string,
    body: unknown | null,
    options?: {
      headers?:
        | HttpHeaders
        | {
            [header: string]: string | string[];
          };
      context?: HttpContext;
      observe?: 'body';
      params?:
        | HttpParams
        | {
            [param: string]:
              | string
              | number
              | boolean
              | ReadonlyArray<string | number | boolean>;
          };
      reportProgress?: boolean;
      responseType?: 'json';
      withCredentials?: boolean;
      timeout?: number;
    }
  ): Observable<UnwrapRestData<T>> {
    const requestOptions = this.addSSRHeader({
      ...options,
      observe: 'body' as const,
      responseType: 'json' as const,
      timeout: options?.timeout || this.DEFAULT_TIMEOUT,
    });

    return this.http
      .put<T>(this.apiURL(url), body, {
        ...requestOptions,
        observe: 'body' as const,
      })
      .pipe(
        timeout(this.DEFAULT_TIMEOUT),
        catchError(this.handleError),
        map((response: T): UnwrapRestData<T> => {
          // Auto-unwrap SingleRestData<T> and RestData<T>
          if (response && typeof response === 'object' && 'data' in response) {
            return response.data as UnwrapRestData<T>;
          }
          return response as UnwrapRestData<T>;
        })
      );
  }

  patch<T extends SingleRestData<unknown> | RestData<unknown>>(
    url: string,
    body: unknown | null,
    options?: {
      headers?:
        | HttpHeaders
        | {
            [header: string]: string | string[];
          };
      context?: HttpContext;
      observe?: 'body';
      params?:
        | HttpParams
        | {
            [param: string]:
              | string
              | number
              | boolean
              | ReadonlyArray<string | number | boolean>;
          };
      reportProgress?: boolean;
      responseType?: 'json';
      withCredentials?: boolean;
      timeout?: number;
    }
  ): Observable<UnwrapRestData<T>> {
    const requestOptions = this.addSSRHeader({
      ...options,
      observe: 'body' as const,
      responseType: 'json' as const,
      timeout: options?.timeout || this.DEFAULT_TIMEOUT,
    });

    return this.http
      .patch<T>(this.apiURL(url), body, {
        ...requestOptions,
        observe: 'body' as const,
      })
      .pipe(
        timeout(this.DEFAULT_TIMEOUT),
        catchError(this.handleError),
        map((response: T): UnwrapRestData<T> => {
          // Auto-unwrap SingleRestData<T> and RestData<T>
          if (response && typeof response === 'object' && 'data' in response) {
            return response.data as UnwrapRestData<T>;
          }
          return response as UnwrapRestData<T>;
        })
      );
  }

  delete<T extends SingleRestData<unknown> | RestData<unknown>>(
    url: string,
    body?: unknown | null,
    options?: {
      headers?:
        | HttpHeaders
        | {
            [header: string]: string | string[];
          };
      context?: HttpContext;
      observe?: 'body';
      params?:
        | HttpParams
        | {
            [param: string]:
              | string
              | number
              | boolean
              | ReadonlyArray<string | number | boolean>;
          };
      reportProgress?: boolean;
      responseType?: 'json';
      withCredentials?: boolean;
      timeout?: number;
    }
  ): Observable<UnwrapRestData<T>> {
    const enhancedOptions = this.addSSRHeader({
      ...(options || {}),
      observe: 'body' as const,
      responseType: 'json' as const,
      timeout: options?.timeout || this.DEFAULT_TIMEOUT,
    });

    const requestOptions = {
      ...enhancedOptions,
      body,
    };

    return this.http
      .delete<T>(this.apiURL(url), {
        ...requestOptions,
        observe: 'body' as const,
      })
      .pipe(
        timeout(this.DEFAULT_TIMEOUT),
        catchError(this.handleError),
        map((response: T): UnwrapRestData<T> => {
          // Auto-unwrap SingleRestData<T> and RestData<T>
          if (response && typeof response === 'object' && 'data' in response) {
            return response.data as UnwrapRestData<T>;
          }
          return response as UnwrapRestData<T>;
        })
      );
  }

  // Base utility methods
  protected apiURL(url: string): string {
    const baseUrl = this.apiUrl.endsWith('/')
      ? this.apiUrl.slice(0, -1)
      : this.apiUrl;
    const endpoint = url.startsWith('/') ? url : `/${url}`;
    return `${baseUrl}${endpoint}`;
  }

  protected addSSRHeader(
    options: Record<string, unknown>
  ): Record<string, unknown> {
    // Basic implementation - just add withCredentials for session cookies
    return {
      ...options,
      withCredentials: true,
    };
  }

  protected handleError = (error: HttpErrorResponse): Observable<never> => {
    // Preserve the original HttpErrorResponse so consumers can access the full error body
    // This allows proper handling of i18n error messages from the API
    return throwError(() => error);
  };
}
